import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from "../app/redux/store";
import { selectIsAuthenticated } from '../app/redux/slices/authSlice';
import { allProbeDataFromAccount, fetchUserInfo, selectCurrentTeam } from "../app/redux/slices/dataSlice";
import Navigation from '../app/components/NavigationSPA';
import ProbeListSession from "../app/components/ProbeListSession";
import ProbeDataSession from "../app/components/ProbeDataSession";
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

export default function SessionPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [sessionPageNum, setSessionPageNum] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation(['session', 'common']);

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentTeam = useSelector(selectCurrentTeam);

  const resetSessionPageNum = () => {
    setSessionPageNum(1);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setIsLoading(false);
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      
      try {
        // First fetch user info to establish currentTeam if not already set
        if (!currentTeam?.name) {
          logger.log("Fetching user info to establish currentTeam...");
          await dispatch(fetchUserInfo()).unwrap();
        }
        
        if (currentTeam?.name) {
          const msg = {
            teamName: currentTeam.name,
          };
          try {
            await dispatch(allProbeDataFromAccount(msg)).unwrap();
          } catch (apiError) {
            logger.error("API error fetching probe data:", apiError);
          }
        } else {
          logger.log("No currentTeam available");
        }
      } catch (error) {
        logger.error("Error fetching session data:", error);
      }
    };

    fetchData();
  }, [currentTeam?.name, dispatch, isAuthenticated]);

  const resetSessionPage = () => {
    if (currentTeam?.name) {
      const msg = {
        teamName: currentTeam.name,
      };
      dispatch(allProbeDataFromAccount(msg));
      setSessionPageNum(1);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-gray-800">{t('common:loading')}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('session:pageTitle')}</h1>
          <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-200px)]">
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200">
            <ProbeListSession 
              sessionPageNum={sessionPageNum} 
              resetSessionPageNum={resetSessionPageNum}
              currentTeam={currentTeam}
            />
            </div>
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200">
              <ProbeDataSession 
                sessionPageNum={sessionPageNum} 
                setSessionPageNum={setSessionPageNum} 
                resetSessionPage={resetSessionPage}
              />     
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
